import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useState} from "react";
import appStyle from "../../style.less";
import ProgressBar from "@yaak/components/src/ProgressBar";
import {Typography} from "@mui/material";
import Grid from "@yaak/components/src/Grid";
import {
  CreateFeatureFlag,
  createFeatureFlag,
  FeatureFlagAdmin,
  getFeatureFlagAdmin,
  updateFeatureFlag
} from "@yaak/components/services/api/api";
import {transpose} from "../../helpers/common";
import {
  GridActiveElement,
  GridEditActionData,
  GridEditIconElement,
  GridTextElement,
  GridToggleIconElement
} from "@yaak/components/src/Grid/GridElements";
import style from "../PartnerOverview/style.less";
import {TypographyTypes} from "@yaak/components/src/Typography/Typography";
import Divider from "@yaak/components/src/Divider";
import FeatureFlagEditDialog, {
  descriptionDialogInput,
  enabledDialogInput,
  nameDialogInput
} from "./dialogs/FeatureFlagEditDialog/FeatureFlagEditDialog";
import FormDialog from "@yaak/components/src/FormDialog";
import FlagIcon from "@mui/icons-material/Flag";
import SubjectIcon from "@mui/icons-material/Subject";
import {ToastContext, ToastContextType} from "@yaak/components/context/toastContext";
import Button from "@yaak/components/src/Button";


interface FeatureFlagPageProps {
  token: string
}

interface FeatureFlagActions {
  edit: (data: FeatureFlagAdmin) => void
}

const FeatureFlagPage: React.FunctionComponent<FeatureFlagPageProps> = ({
  token,
}): ReactElement => {
  const { setShowToast } = useContext(ToastContext) as ToastContextType

  const [loading, setLoading] = useState(true)
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagAdmin[]>([])
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlagAdmin | null>(null)
  const gridHeaders = ['Name', 'Description', 'Enabled', '', '']
  const [creating, setCreating] = useState(false)

  const editAction = useCallback((flag: GridEditActionData) => {
    setSelectedFlag(flag as FeatureFlagAdmin)
  }, [])

  const toConfirmFeatureFlagCreation = (result: CreateFeatureFlag) => [
    { icon: <FlagIcon/>, value: result.name },
    { icon: <SubjectIcon/>, value: result.description },
  ]

  const onCreate = async (featureFlag: CreateFeatureFlag) => {
    if (featureFlag) {
      setCreating(false)
      const createdFlag = await createFeatureFlag({
        token,
        createFeatureFlag: featureFlag,
        onAlert: setShowToast,
      })

      createdFlag && setFeatureFlags(featureFlags.concat(createdFlag))
    }
  }

  useEffect(() => {
    const fetchFeatureFlag = async () => {
      if (!token) {
        return
      }

      const flags = await getFeatureFlagAdmin({
        token,
      })
      setLoading(false)
      setFeatureFlags(flags)
    }

    fetchFeatureFlag();
  }, [token])

  const getRows = (
    featureFlags: FeatureFlagAdmin[],
    actions: FeatureFlagActions
  ) =>
    featureFlags?.map((featureFlag) => {
      const row = []
      row.push(
        GridTextElement({
          text: featureFlag.name,
        })
      )

      row.push(GridTextElement({
          text: featureFlag.description || 'No description provided',
        })
      )

      row.push(
        GridActiveElement({
          condition: featureFlag.enabled,
        })
      )

      //
      row.push(
        GridToggleIconElement({
          data: featureFlag,
          toggleAction: async (flag: FeatureFlagAdmin) => {
            const updatedFlag = {
              ...flag,
              enabled: !flag.enabled,
            }

            const updatedFeatureFlag = await updateFeatureFlag({
              token,
              featureFlag: {
                id: flag.id,
                enabled: !flag.enabled,
              },
            })

            updatedFeatureFlag && setFeatureFlags(
              featureFlags.map((p) => (p.id === updatedFlag.id ? updatedFlag : p))
            )
          }
        })
      )

      row.push(
        GridEditIconElement({
          data: featureFlag,
          name: 'feature flag',
          editAction: actions.edit,
        })
      )
      return row
    })

  const getMappedData = ({
    featureFlags,
    actions
  }: {
    featureFlags: FeatureFlagAdmin[]
    actions: FeatureFlagActions
  }) => {
    const columns = transpose(
      getRows(featureFlags, actions)
    )
    columns.map((row: any, i: number) =>
      gridHeaders.map((header, k) => i === k && row.unshift(header))
    )
    return {
      rows: columns,
    }
  }

  const mappedData = useMemo(() => {
    return getMappedData({
      featureFlags,
      actions: {
        edit: editAction,
      }
    })
  }, [featureFlags])

  const onEdit = async (flag: FeatureFlagAdmin) => {
    const flagToUpdate = {
      ...selectedFlag,
      ...flag,
    }

    await updateFeatureFlag({
      token,
      featureFlag: {
        id: flagToUpdate.id,
        description: flagToUpdate.description,
        enabled: flagToUpdate.enabled,
      },
    })

    setFeatureFlags(
      featureFlags.map((p) => (p.id === flagToUpdate.id ? flagToUpdate : p))
    )
    setSelectedFlag(null)
  }

  return (
    <div className={appStyle.page}>
      <Typography type={TypographyTypes.headline}>{'Feature Flags'}</Typography>
      <Divider />
      {loading ? (
        <ProgressBar />
      ) : (
        <>
          <div className={appStyle.actionBar}>
            <div className={style.actionBarButtonBox}>
              <Button
                onClick={() => setCreating(true)}
                text={'Add Feature Flag'}
              ></Button>
            </div>
          </div>
          <div className={style.partnerPageTableWrapper}>
            <Grid
              headers={gridHeaders}
              data={mappedData}
              headerSort={['Name']}
            />
          </div>
        </>
      )}

      {/* Create Dialog */}
      <FormDialog
        titleIcon={<FlagIcon/>}
        title={'Add Feature Flag'}
        inputs={() =>
          Promise.resolve([
            nameDialogInput,
            descriptionDialogInput,
            enabledDialogInput,
          ])
        }
        open={creating}
        confirmationTitle={'Confirm details'}
        toDetailEntries={toConfirmFeatureFlagCreation}
        onCancel={() => setCreating(false)}
        onConfirmed={onCreate}
      />

      {/* Create Dialog */}
      <FeatureFlagEditDialog
        token={token}
        selectedFeatureFlag={selectedFlag}
        onCancel={() => setSelectedFlag(null)}
        onConfirmed={onEdit}
      />
    </div>
  )
}

export default FeatureFlagPage;
